import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import Head from '@/components/base/Head';

interface FormState {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  privacy: boolean;
}

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState<FormState>({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    privacy: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const subjects = [
    t('contact.subject_dental'),
    t('contact.subject_surgery'),
    t('contact.subject_hair'),
    t('contact.subject_general'),
    t('contact.subject_price'),
    t('contact.subject_other'),
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'message') setCharCount(value.length);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.privacy) return;
    setLoading(true);

    // Send the contact message to the clinic inbox (hello@esteva.ch) + a
    // confirmation to the visitor, via the Supabase send-contact edge function.
    try {
      const edgeUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-contact`;
      await fetch(edgeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          firstname: form.firstname,
          lastname: form.lastname,
          email: form.email,
          phone: form.phone,
          subject: form.subject,
          message: form.message,
          language: i18n.language.split('-')[0],
        }),
      });
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <>
      <Head
        title={t('seo.contact.title')}
        metas={[
          { name: 'description', content: t('seo.contact.description') },
          { name: 'keywords', content: t('seo.contact.keywords') },
          { property: 'og:title', content: t('seo.contact.title') },
          { property: 'og:description', content: t('seo.contact.description') },
          { property: 'og:url', content: 'https://estevaclinic.com/contact' },
          { property: 'og:type', content: 'website' },
          { property: 'og:locale', content: t('seo.ogLocale') },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: t('seo.contact.title') },
          { name: 'twitter:description', content: t('seo.contact.description') },
          { name: 'last-modified', content: '2026-06-11' },
        ]}
        links={[{ rel: 'canonical', href: 'https://estevaclinic.com/contact' }]}
      />

      <main className="bg-background-50 min-h-screen">
        <Navbar />

        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 px-6 md:px-10 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1060 50%, #1a0a2e 100%)' }}>
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20luxury%20medical%20clinic%20reception%20area%20with%20soft%20warm%20purple%20and%20cream%20lighting%2C%20elegant%20marble%20floor%20with%20subtle%20reflections%2C%20modern%20minimalist%20interior%20design%2C%20blurred%20background%20with%20bokeh%20effect%2C%20premium%20healthcare%20atmosphere%2C%20sophisticated%20and%20calming%20ambiance&width=1600&height=500&seq=contact-hero-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(26,10,46,0.85) 0%, rgba(45,16,96,0.75) 50%, rgba(26,10,46,0.85) 100%)' }}></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <span
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase font-label text-white mb-5"
                style={{ background: 'rgba(192,132,252,0.2)', border: '1px solid rgba(192,132,252,0.4)' }}
              >
                <i className="ri-customer-service-2-line text-xs"></i>
                {t('contact.hero_tag')}
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
                {t('contact.hero_title_line1')}
                <br />
                <span className="italic font-light text-white/70">{t('contact.hero_title_line2')}</span>
              </h1>
              <p className="mt-4 text-sm md:text-base text-white/55 font-body leading-relaxed max-w-lg">
                {t('contact.hero_subtitle')}
              </p>
            </div>

            <div className="flex flex-wrap gap-4 md:gap-6 mt-8 md:mt-10">
              <a href="tel:+41765593993" className="flex items-center gap-2.5 cursor-pointer group no-underline">
                <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: 'rgba(192,132,252,0.2)', border: '1px solid rgba(192,132,252,0.4)' }}>
                  <i className="ri-phone-line text-xs" style={{ color: 'rgba(192,132,252,0.9)' }}></i>
                </div>
                <span className="text-xs text-white/70 font-body group-hover:text-white transition-colors">+41 76 559 39 93</span>
              </a>
              <a href="mailto:info@esteva.ch" className="flex items-center gap-2.5 cursor-pointer group no-underline">
                <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: 'rgba(192,132,252,0.2)', border: '1px solid rgba(192,132,252,0.4)' }}>
                  <i className="ri-mail-line text-xs" style={{ color: 'rgba(192,132,252,0.9)' }}></i>
                </div>
                <span className="text-xs text-white/70 font-body group-hover:text-white transition-colors">info@esteva.ch</span>
              </a>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: 'rgba(192,132,252,0.2)', border: '1px solid rgba(192,132,252,0.4)' }}>
                  <i className="ri-time-line text-xs" style={{ color: 'rgba(192,132,252,0.9)' }}></i>
                </div>
                <span className="text-xs text-white/70 font-body">{t('contact.monday_friday')} 08:00–20:00</span>
              </div>
            </div>
          </div>
        </section>

        {/* Main content */}
        <section className="py-12 md:py-20 px-4 md:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12">

              {/* Form */}
              <div className="lg:col-span-3">
                <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-10">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-1">
                    {t('contact.form_title')}
                  </h2>
                  <p className="text-xs text-foreground-400 font-body mb-7">
                    {t('contact.form_required_hint')}
                  </p>

                  {submitted ? (
                    <div className="flex flex-col items-center justify-center py-14 text-center">
                      <div
                        className="w-16 h-16 flex items-center justify-center rounded-full mb-5"
                        style={{ background: 'rgba(192,132,252,0.15)', border: '1px solid rgba(192,132,252,0.4)' }}
                      >
                        <i className="ri-check-double-line text-2xl" style={{ color: 'rgba(168,85,247,0.9)' }}></i>
                      </div>
                      <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">
                        {t('contact.success_title')}
                      </h3>
                      <p className="text-sm text-foreground-500 font-body max-w-xs leading-relaxed">
                        {t('contact.success_message')}
                      </p>
                      <button
                        onClick={() => {
                          setSubmitted(false);
                          setForm({ firstname: '', lastname: '', email: '', phone: '', subject: '', message: '', privacy: false });
                          setCharCount(0);
                        }}
                        className="mt-6 text-xs font-label tracking-[0.1em] uppercase cursor-pointer hover:underline"
                        style={{ color: 'rgba(168,85,247,0.9)' }}
                      >
                        {t('contact.new_message')}
                      </button>
                    </div>
                  ) : (
                    <form
                      id="contact-esteva-form"
                      onSubmit={handleSubmit}
                      className="space-y-5"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                            {t('contact.firstname_label')} *
                          </label>
                          <input
                            type="text"
                            name="firstname"
                            required
                            value={form.firstname}
                            onChange={handleChange}
                            placeholder={t('contact.firstname_label')}
                            className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none transition-all"
                            onFocus={(e) => { e.target.style.borderColor = 'rgba(168,85,247,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.1)'; }}
                            onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                            {t('contact.lastname_label')} *
                          </label>
                          <input
                            type="text"
                            name="lastname"
                            required
                            value={form.lastname}
                            onChange={handleChange}
                            placeholder={t('contact.lastname_label')}
                            className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none transition-all"
                            onFocus={(e) => { e.target.style.borderColor = 'rgba(168,85,247,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.1)'; }}
                            onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                            {t('imprint.email_label')} *
                          </label>
                          <input
                            type="email"
                            name="email"
                            required
                            value={form.email}
                            onChange={handleChange}
                            placeholder="info@example.com"
                            className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none transition-all"
                            onFocus={(e) => { e.target.style.borderColor = 'rgba(168,85,247,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.1)'; }}
                            onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                            {t('contact.phone_optional')}
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="+49 123 456789"
                            className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none transition-all"
                            onFocus={(e) => { e.target.style.borderColor = 'rgba(168,85,247,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.1)'; }}
                            onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                          {t('contact.subject_label')} *
                        </label>
                        <select
                          name="subject"
                          required
                          value={form.subject}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body focus:outline-none transition-all appearance-none cursor-pointer"
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(168,85,247,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.1)'; }}
                          onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                        >
                          <option value="">{t('contact.subject_placeholder')}</option>
                          {subjects.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                          {t('contact.message_label')} * <span className="text-foreground-400 font-normal">{t('contact.message_max')}</span>
                        </label>
                        <textarea
                          name="message"
                          required
                          rows={5}
                          maxLength={500}
                          value={form.message}
                          onChange={handleChange}
                          placeholder={t('contact.message_label')}
                          className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none transition-all resize-none"
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(168,85,247,0.6)'; e.target.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.1)'; }}
                          onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                        />
                        <p className="mt-1 text-xs text-foreground-400 font-body text-right">
                          {charCount}/500
                        </p>
                      </div>

                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          name="privacy"
                          checked={form.privacy}
                          onChange={handleChange}
                          required
                          className="mt-0.5 cursor-pointer"
                          style={{ accentColor: 'rgba(168,85,247,0.9)' }}
                        />
                        <span className="text-xs text-foreground-500 font-body leading-relaxed group-hover:text-foreground-700 transition-colors">
                          {t('contact.privacy_contact')} *{' '}
                          <Link to="/privacy" target="_blank" className="text-primary-600 underline hover:text-primary-700">
                            {t('footer.privacy')}
                          </Link>
                          {' · '}
                          <Link to="/agb" target="_blank" className="text-primary-600 underline hover:text-primary-700">
                            {t('footer.terms')}
                          </Link>
                        </span>
                      </label>

                      <button
                        type="submit"
                        disabled={loading || !form.privacy}
                        className="w-full py-4 rounded-full text-white text-sm tracking-[0.12em] uppercase font-label font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 hover:scale-[1.01]"
                        style={{ background: loading ? 'rgba(126,34,206,0.5)' : 'rgba(126,34,206,0.9)' }}
                      >
                        {loading ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-lg"></i>
                            {t('contact.sending')}
                          </>
                        ) : (
                          <>
                            {t('contact.submit')}
                            <i className="ri-send-plane-line text-lg"></i>
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>

              {/* Right column: Info + Map */}
              <div className="lg:col-span-2 space-y-6">

                {/* Info card */}
                <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-8">
                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-6">
                    {t('contact.info_title')}
                  </h3>
                  <div className="space-y-5">
                    <div className="flex items-start gap-3.5">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(192,132,252,0.12)', border: '1px solid rgba(192,132,252,0.25)' }}>
                        <i className="ri-map-pin-2-line text-sm" style={{ color: 'rgba(168,85,247,0.85)' }}></i>
                      </div>
                      <div>
                        <p className="text-[10px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-1">{t('contact.address_label')}</p>
                        <p className="text-sm text-foreground-700 font-body leading-relaxed">
                          Esteva Clinic<br />Rruga Bylis<br />Tiranë 1001, Albania
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3.5">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(192,132,252,0.12)', border: '1px solid rgba(192,132,252,0.25)' }}>
                        <i className="ri-phone-line text-sm" style={{ color: 'rgba(168,85,247,0.85)' }}></i>
                      </div>
                      <div>
                        <p className="text-[10px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-1">{t('imprint.phone_label')}</p>
                        <a href="tel:+41765593993" className="text-sm font-body cursor-pointer no-underline hover:underline" style={{ color: 'rgba(126,34,206,0.85)' }}>
                          +41 76 559 39 93
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3.5">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(192,132,252,0.12)', border: '1px solid rgba(192,132,252,0.25)' }}>
                        <i className="ri-mail-line text-sm" style={{ color: 'rgba(168,85,247,0.85)' }}></i>
                      </div>
                      <div>
                        <p className="text-[10px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-1">{t('imprint.email_label')}</p>
                        <a href="mailto:info@esteva.ch" className="text-sm font-body cursor-pointer no-underline hover:underline" style={{ color: 'rgba(126,34,206,0.85)' }}>
                          info@esteva.ch
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3.5">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(192,132,252,0.12)', border: '1px solid rgba(192,132,252,0.25)' }}>
                        <i className="ri-time-line text-sm" style={{ color: 'rgba(168,85,247,0.85)' }}></i>
                      </div>
                      <div>
                        <p className="text-[10px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-1">{t('contact.opening_hours')}</p>
                        <div className="space-y-0.5">
                          <p className="text-sm text-foreground-700 font-body">{t('contact.monday_friday')}: 08:00 – 20:00</p>
                          <p className="text-sm text-foreground-700 font-body">{t('contact.saturday')}: 09:00 – 16:00</p>
                          <p className="text-sm text-foreground-400 font-body italic">{t('contact.sunday')}: {t('contact.closed')}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Social links */}
                  <div className="mt-6 pt-5 border-t border-background-200">
                    <p className="text-[10px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-3">{t('contact.follow_us')}</p>
                    <div className="flex items-center gap-3">
                      <a
                        href="https://www.instagram.com/estevaclinic"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 flex items-center justify-center rounded-full text-white hover:scale-110 transition-all cursor-pointer"
                        style={{ background: 'linear-gradient(135deg, #f09433 0%, #dc2743 50%, #bc1888 100%)' }}
                        aria-label="Instagram"
                      >
                        <i className="ri-instagram-line text-sm"></i>
                      </a>
                      <a
                        href="#"
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-600 hover:bg-foreground-200 hover:scale-110 transition-all cursor-pointer"
                        aria-label="TikTok"
                      >
                        <i className="ri-tiktok-fill text-sm"></i>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="rounded-2xl overflow-hidden border border-background-200/70 h-[280px] md:h-[360px]">
                  <iframe
                    src="https://maps.google.com/maps?q=Rruga%20Bylis,%20Tiran%C3%AB%201001,%20Albania&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    title="Esteva Clinic"
                    className="w-full h-full"
                  ></iframe>
                </div>

                {/* Direct booking CTA */}
                <a
                  href="/booking"
                  className="flex items-center justify-between gap-4 p-5 rounded-2xl text-white transition-all hover:opacity-90 cursor-pointer no-underline group"
                  style={{ background: 'linear-gradient(135deg, rgba(45,16,96,0.95) 0%, rgba(126,34,206,0.85) 100%)' }}
                >
                  <div>
                    <p className="text-xs tracking-[0.15em] uppercase font-label text-white/60 mb-0.5">{t('contact.book_title')}</p>
                    <p className="text-sm font-label font-semibold text-white">{t('contact.book_online')}</p>
                    <p className="text-xs text-white/55 font-body mt-0.5">{t('contact.book_free')}</p>
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 group-hover:bg-white/25 transition-colors flex-shrink-0">
                    <i className="ri-calendar-check-line text-white text-lg"></i>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Full-width map section */}
        <section className="px-4 md:px-10 pb-16 md:pb-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div className="min-w-0">
                <span className="text-[10px] tracking-[0.2em] uppercase font-label text-foreground-400">{t('contact.find_us_label')}</span>
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-0.5 break-words">Esteva Clinic – Euromed Hospital, Tiranë</h3>
              </div>
              <a
                href="https://www.google.com/maps/search/Rruga+Bylis,+Tiran%C3%AB+1001,+Albanien"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center self-start gap-2 px-4 py-2 rounded-full text-xs font-label tracking-[0.1em] uppercase cursor-pointer whitespace-nowrap no-underline transition-all hover:opacity-80"
                style={{ background: 'rgba(126,34,206,0.1)', color: 'rgba(126,34,206,0.9)', border: '1px solid rgba(126,34,206,0.25)' }}
              >
                <i className="ri-map-pin-2-line text-sm"></i>
                {t('contact.maps_open')}
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden border border-background-200/70 w-full h-[400px] md:h-[480px]">
              <iframe
                src="https://maps.google.com/maps?q=Rruga%20Bylis,%20Tiran%C3%AB%201001,%20Albania&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title={t('info.map_title')}
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}